import { useAuthState } from '@/app/providers/auth'
import Link, { useLocale, useLocaleLink } from '@/components/Link'
import getLocalizedHref from '@/components/Link/getLocalizedHref'
import { UserRole } from '@/generated/graphql'
import { getTenantLinkComponentRule } from '@/helpers/tenants'
import { SPLITS, useFeatureFlag } from '@/helpers/useFeatureFlag'
import useTenant from '@/hooks/useTenant'
import Dropdown, { DropdownLink } from '@caterdesk/ui--dropdown'
import { resolveLogoVariant } from '@caterdesk/ui--feedr-logo'
import { main } from '@caterdesk/ui--theme'
import { ShoppingCart } from '@mui/icons-material'
import { Box, Button, Container, Stack, Tooltip, useMediaQuery, useTheme } from '@mui/material'

import Image from 'next/image'
import { usePathname } from 'next/navigation'
import type React from 'react'
import { useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { match } from 'ts-pattern'

import { loggedInLinks, getLoggedInManagerLinks } from './constants'
import { CountryDropdown } from './countryDropdown'
import Mobile from './mobile'
import RequestAQuoteButton from './requestAQuoteButton'
import { SignedInDropdown } from './signedInDropdown'

type Props = {
  previewData?: { itemQuantity: number; grandTotal: string } | null
  newItemWasJustAdded?: boolean
  onHeightChange?: (height: number) => void
  onClickBasketPreview?: () => void
}

const Navigation: React.FC<Props> = ({
  previewData,
  newItemWasJustAdded,
  onClickBasketPreview,
  onHeightChange,
}) => {
  const tenant = useTenant()
  const locale = useLocale()
  const pathname = usePathname()
  const theme = useTheme()

  const generateLocaleLink = useLocaleLink()
  const authState = useAuthState()
  const { t } = useTranslation()

  const ref = useRef<HTMLDivElement>(null)
  const isMobile = useMediaQuery(theme.breakpoints.down('md'))

  const showCountryDropdown = useFeatureFlag(SPLITS.MARKETPLACE_COUNTRY_DROPDOWN)
  const showNewSignedInDropdown = useFeatureFlag(SPLITS.NEW_MARKETPLACE_SIGNED_IN_DROPDOWN)
  const sidebarNavEnabled = useFeatureFlag(SPLITS.DASHBOARD_SIDEBAR_NAV)

  const showBasketPreview = previewData
  const itemCount = previewData?.itemQuantity ?? 0
  const grandTotal = previewData?.grandTotal ?? ''

  const homepageRule = getTenantLinkComponentRule('/', tenant.domain, true)

  const handleMobileClick = () => {
    if (!onClickBasketPreview) return
    onClickBasketPreview()
  }

  const handleDesktopClick = () => {
    const orderDetailsTop = document.getElementById('input-bar')

    if (orderDetailsTop) {
      const offset = 8
      const orderDetailsPosition = orderDetailsTop.getBoundingClientRect().top + window.scrollY
      const offsetPosition = orderDetailsPosition - offset

      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth',
      })
    }
  }

  const handleBasketIconClicked = () => {
    if (isMobile) {
      handleMobileClick()
    } else {
      handleDesktopClick()
    }
  }

  useEffect(() => {
    const updateHeight = () => {
      if (ref.current && onHeightChange) {
        onHeightChange(ref.current.offsetHeight)
      }
    }

    const observer = new ResizeObserver(updateHeight)

    if (ref.current) observer.observe(ref.current)

    return () => observer.disconnect()
  }, [])

  return (
    <Stack
      ref={ref}
      sx={{
        display: 'block',
        position: 'sticky',
        top: 0,
        zIndex: 30,
        background: '#fff',
        boxShadow: '0px 2px 10px 0px rgba(0, 0, 0, 0.05)',
        padding: '12px 16px',
        md: { padding: '16px 32px' },
      }}>
      <Container component="nav" disableGutters sx={{ maxWidth: { xs: 'none', xl: 'none' } }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <Stack direction="row" alignItems="center" spacing={{ xs: '6px', md: '10px' }}>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'text.primary',
                fontSize: { xs: '14px', md: '16px' },
                fontWeight: 500,
                '& > div': {
                  position: 'relative',
                  top: '8px',
                },
              }}>
              <Link href={getLocalizedHref(homepageRule.uri, locale)}>
                <Image
                  src={resolveLogoVariant({
                    setToRainbowDuringPride: true,
                    logos: tenant.marketing.logos,
                  })}
                  alt={tenant.name}
                  height={isMobile ? 25 : 50}
                  width={isMobile ? 75 : 150}
                  style={{
                    height: isMobile ? 25 : 50,
                    width: isMobile ? 75 : 150,
                    objectFit: 'contain',
                  }}
                  priority
                />
              </Link>
            </Box>

            {!isMobile && (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: 'text.primary',
                  fontSize: { xs: '14px', md: '16px' },
                  fontWeight: 500,
                  '& > div': {
                    position: 'relative',
                    top: '8px',
                  },
                }}>
                {t('marketplace:catering')}
              </Box>
            )}
          </Stack>

          <Stack direction="row" alignItems="center" spacing={{ xs: '6px', md: '10px' }}>
            <RequestAQuoteButton />

            {showBasketPreview && (
              <Stack>
                <Tooltip
                  title={
                    <>
                      <div>{t('marketplace:addedToBasket')}</div>
                      <div>
                        {t('common:subtotal')}: {grandTotal}
                      </div>
                    </>
                  }
                  placement="bottom"
                  open={(isMobile && newItemWasJustAdded) || false}>
                  <Button
                    data-cy="ShoppingCartIcon"
                    type="button"
                    variant="contained"
                    color={itemCount > 0 ? 'primary' : 'secondary'}
                    size="medium"
                    disabled={!isMobile}
                    onClick={handleBasketIconClicked}
                    startIcon={<ShoppingCart sx={{ ml: 1, px: '1px' }} />}
                    sx={{
                      pl: 1,
                      '&.Mui-disabled': {
                        bgcolor: 'inverted.main',
                        color: 'inverted.contrastText',
                        border: '1px solid',
                        borderColor: 'inverted.dark',
                        py: '5px',
                      },
                    }}>
                    {isMobile
                      ? itemCount
                      : `${itemCount} ${itemCount === 1 ? t('marketplace:item') : t('marketplace:items')}, ${grandTotal}`}
                  </Button>
                </Tooltip>
              </Stack>
            )}

            {showCountryDropdown ? <CountryDropdown /> : null}

            {isMobile ? (
              <Mobile />
            ) : (
              match({ authState, tenant })
                .with({ tenant: { permissions: { allowUserSignIn: false } } }, () => null)
                .with({ authState: { type: 'authenticated' } }, ({ authState: { user } }) =>
                  showNewSignedInDropdown ? (
                    <SignedInDropdown user={user} />
                  ) : (
                    <Stack>
                      <Dropdown title={`${user.firstName} ${user.lastName}`}>
                        {(user.role === UserRole.Manager
                          ? getLoggedInManagerLinks(sidebarNavEnabled)
                          : loggedInLinks
                        ).map((link) => (
                          <DropdownLink
                            key={link.name}
                            name={link.name}
                            color={link.color || main.colors.text}
                            href={generateLocaleLink(link.url)}
                          />
                        ))}
                      </Dropdown>
                    </Stack>
                  ),
                )
                .with(
                  { authState: { type: 'loading' } },
                  { authState: { type: 'unauthenticated' } },
                  () => (
                    <Stack>
                      <Link href={`/authentication/login?destination=${pathname}`} passHref>
                        <Button variant="contained" size="medium">
                          {t('marketplace:signIn')}
                        </Button>
                      </Link>
                    </Stack>
                  ),
                )
                .exhaustive()
            )}
          </Stack>
        </Stack>
      </Container>
    </Stack>
  )
}

export default Navigation
